import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { first } from 'rxjs/operators';
import { AzureAccountsService } from 'src/app/core/http-services/azure-accounts.service';
import { AzureNamespacesService } from 'src/app/core/http-services/azure-namespaces.service';
import { LocationService } from 'src/app/core/http-services/location.service';
import { AzureOperationsService } from 'src/app/core/services/azure-operations.service';
import { AzureTokenService } from 'src/app/core/services/azure-token.service';
import { AzureAccount } from 'src/app/shared/models/azure-accounts.model';
import { AzureNamespace, AzureNamespaceDisplay, NamespaceAvailableOnTheAzureResponse, UpdateActiveStatusRequest } from 'src/app/shared/models/azure-namespaces.model';
import { AzureErrorResponse } from 'src/app/shared/models/azure-token.model';
import { PCLocation } from 'src/app/shared/models/location.model';

@Component({
  selector: 'app-edit-namespace',
  templateUrl: './edit-namespace.component.html',
  styleUrls: ['./edit-namespace.component.css']
})
export class EditNamespaceComponent implements OnInit {
  isAccountSelected: boolean = true;
  isNamespaceEntered: boolean = true;
  isResourceGroupEntered: boolean = true;
  isLocationSelected: boolean = true;
  azureAccounts: AzureAccount[];
  selectedAzureAccount: AzureAccount = null;
  locations: PCLocation[];
  selectedLocation: PCLocation;

  @Input()
  currentNamespace: AzureNamespaceDisplay;

  @Output()
  isNamespaceSaved: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  azureNamespaceId: number;

  error: string;
  isErrorVisible: boolean = false;
  isSuccessVisible: boolean = false;
  success: string = '';

  constructor(private formBuilder: FormBuilder,
    private azureNamespaceService: AzureNamespacesService,
    private azureAccountService: AzureAccountsService,
    private locationService: LocationService,
    private router: Router,
    private azureOperationsService: AzureOperationsService,
    private azureTokenService: AzureTokenService,
    public activeModal: NgbActiveModal) {
    this.buildForm();
    this.getAccounts();
  }

  ngOnInit(): void {
    this.getLocations();

    //THIS WILL CLOSE THE POPUP IF USER IS FORCED LOGGED OUT
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        var url = window.location.toString();
        if (url.includes('/login'))
          this.activeModal.close();
      }
    });
  }

  namespaceForm: FormGroup;

  private buildForm(): void {
    this.namespaceForm = this.formBuilder.group({
      azurenamespace: [null, Validators.required],
      resourceGroup: [null, Validators.required],
      checkActive: [null],
    });
  }

  getLocations(id: number = 0) {
    if (this.locations && this.locations.length > 0)
      return;

    this.locationService.getLocations()
      .subscribe((dataList: PCLocation[]) => {
        if (dataList && dataList.length > 0) {
          this.locations = dataList;
          if (id > 0) {
            this.selectedLocation = this.locations.find(l => l.Id === id);
          }
        }
      });
  }

  get f() { return this.namespaceForm.controls; }
  isBusy: boolean = false;
  isLoading: boolean = false;
  isValid: boolean = true;

  public setFormValues() {
    if (!this.currentNamespace) {
      return;
    }

    //DISABLE ALL THE FIELDS SINCE WE ARE IN THE EDIT MODE
    this.f.azurenamespace.disable();
    this.f.resourceGroup.disable();

    this.f.azurenamespace.setValue(this.currentNamespace.AzureNamespace);
    this.f.resourceGroup.setValue(this.currentNamespace.ResourceGroup);
    this.f.checkActive.setValue(this.currentNamespace.Active);

    if (!this.azureAccounts || this.azureAccounts.length == 0) {
      this.azureAccountService.getAccounts(1, 50000)
        .subscribe((dataList: AzureAccount[]) => {
          if (dataList && dataList.length > 0) {
            this.azureAccounts = dataList;
            this.selectedAzureAccount = this.azureAccounts.find(x => x.Id == this.currentNamespace.AzureAccountId);
          }

          //SELECTING LOCATION
          if (!this.locations || this.locations.length == 0) {
            this.getLocations(this.currentNamespace.LocationId);
          }
          else {
            if (this.locations.some(x => x.Id == this.currentNamespace.LocationId)) {
              this.selectedLocation = this.locations.find(x => x.Id == this.currentNamespace.LocationId);
            }
          }

        });
    }
    else {
      if (this.azureAccounts.some(x => x.Id == this.currentNamespace.AzureAccountId)) {
        this.selectedAzureAccount = this.azureAccounts.find(x => x.Id == this.currentNamespace.AzureAccountId);

        //SELECTING LOCATION
        if (!this.locations || this.locations.length == 0) {
          this.getLocations(this.currentNamespace.LocationId);
        }
        else {
          if (this.locations.some(x => x.Id == this.currentNamespace.LocationId)) {
            this.selectedLocation = this.locations.find(x => x.Id == this.currentNamespace.LocationId);
          }
        }
      }
    }



  }


  checkAvailability() {
    this.isErrorVisible = false;
    this.isSuccessVisible = false;

    this.isNamespaceEntered = this.f.azurenamespace.valid;

    this.isAccountSelected = true;

    if (!this.selectedAzureAccount) {
      this.isAccountSelected = false;
    }

    if (!this.isNamespaceEntered || !this.isAccountSelected) {
      return;
    }

    if (this.azureTokenService.isLoggedIn()) {
      //USER IS ALREADY LOGGED IN
      this.checkNamespace();
    }
    else {
      this.azureTokenService.login().then(() => {
        this.checkNamespace();
      }).catch((err) => {
        this.isErrorVisible = true;
        this.error = "Could not log you into Azure account, check developer console for more details";
      });
    }
  }

  private checkNamespace(showSuccess: boolean = true) {

    return new Promise((resolve, reject) => {
      this.azureOperationsService.checkNamespaceAvailability(this.selectedAzureAccount.SubscriptionId
        , this.f.azurenamespace.value)
        .pipe(first())
        .subscribe((result: NamespaceAvailableOnTheAzureResponse) => {
          if (!result.nameAvailable) {
            this.isErrorVisible = true;
            this.error = `${result.reason} : ${result.message}`;
            this.isBusy = false;
            this.isLoading = false;
            resolve(false);
          }
          else {
            if (showSuccess) {
              this.isSuccessVisible = true;
              this.success = 'Namespace available on the Microsoft Azure';
            }
            resolve(true);
          }
        },
          (error: AzureErrorResponse) => {
            this.isBusy = false;
            this.isLoading = false;
            this.isErrorVisible = true;
            this.error = error.message;
            reject(false);
          });
    });
  }

  createEditNamespace() {
    if (this.isBusy)
      return;

    this.isErrorVisible = false;
    this.isSuccessVisible = false;
    this.isBusy = true;
    this.isLoading = true;
    this.isValid = true;


    let isActive: boolean = false;
    if (this.f.checkActive.value != null) {
      isActive = this.f.checkActive.value;
    }

    if (this.azureNamespaceId && this.azureNamespaceId > 0) { //TRYING TO UPDATE ACTIVE STATUS
      let updateRequest = new UpdateActiveStatusRequest();
      updateRequest.id = this.azureNamespaceId;
      updateRequest.active = isActive;

      this.azureNamespaceService.updateActiveStatus(updateRequest)
        .pipe(first())
        .subscribe(
          data => {
            this.isLoading = false;
            this.isBusy = false;

            this.isNamespaceSaved.emit(true);
            this.activeModal.close();
          },
          error => {
            this.error = error;
            this.isErrorVisible = true;
            this.isLoading = false;
            this.isBusy = false;
          }
        )
    }
    else { //TRYING TO CREATE A NEW ACCOUNT
      this.isNamespaceEntered = this.f.azurenamespace.valid;
      this.isResourceGroupEntered = this.f.resourceGroup.valid;

      this.isAccountSelected = true;
      this.isLocationSelected = true;

      if (!this.selectedAzureAccount) {
        this.isAccountSelected = false;
      }

      if (!this.selectedLocation) {
        this.isLocationSelected = false;
      }

      if (!this.isNamespaceEntered || !this.isResourceGroupEntered || !this.isAccountSelected || !this.isLocationSelected) {
        this.isBusy = false;
        this.isLoading = false;
        return;
      }
      let request = new AzureNamespace();
      request.Namespace = this.f.azurenamespace.value;
      request.ResourceGroup = this.f.resourceGroup.value;
      request.AzureAccountIdFk = this.selectedAzureAccount.Id;
      request.LocationIdFk = this.selectedLocation.Id;
      request.Id = 0;
      request.KeyName = '-';
      request.KeyValue = '-';
      request.Active = true;

      if (this.azureTokenService.isLoggedIn()) {
        //USER IS ALREADY LOGGED IN
        this.checkNamespace(false).then((result: boolean) => {
          if (result) {
            //SAVE THE RECORD IN THE DATABASE
            this.createNamespaceOnAzure(request);
          }
        });
      }
      else {
        this.azureTokenService.login().then(() => {
          this.checkNamespace(false).then((result: boolean) => {
            if (result) {
              //CREATE NAMESPACE ON THE AZURE
              this.createNamespaceOnAzure(request);
            }
          });
        }).catch((err) => {
          this.isLoading = false;
          this.isBusy = false;
          this.isErrorVisible = true;
          this.error = "Could not log you into Azure account, check developer console for more details";
        });
      }

    }

  }

  createNamespaceOnAzure(request: AzureNamespace) {
    const subscriptionId = this.selectedAzureAccount.SubscriptionId;
    const namespace = this.f.azurenamespace.value;
    const resourceGroup = this.f.resourceGroup.value;
    const location = this.selectedLocation.LocationValue;
    this.azureOperationsService.createAzureNamespace(subscriptionId, resourceGroup, namespace, location)
      .pipe(first())
      .subscribe((result) => {
        //NOW SAVE RECORD IN THE SQL DATABASE
        this.saveNamespaceInTheDB(request);
      },
        (error: AzureErrorResponse) => {
          this.error = error.message;
          this.isErrorVisible = true;
        })
  }

  saveNamespaceInTheDB(request: AzureNamespace) {
    this.azureNamespaceService.createNamespace(request)
      .pipe(first())
      .subscribe(
        data => {
          this.isLoading = false;
          this.isBusy = false;

          this.isNamespaceSaved.emit(true);
          this.activeModal.close();
        },
        error => {
          this.error = error;
          this.isErrorVisible = true;
          this.isLoading = false;
          this.isBusy = false;
        }
      )
  }

  getAccounts() {
    this.azureAccountService.getAccounts(1, 50000)
      .subscribe((dataList: AzureAccount[]) => {
        if (dataList && dataList.length > 0) {
          this.azureAccounts = dataList;
        }
      });
  }

}
